// EL CATÁLOGO DE ORÍGENES DEL LEAD
//
// Los de fábrica + los que crea la agencia (Lissette 2026-08-28, punto 27: *"en
// la ficha de la póliza donde dice lead source, la opción de crear etiquetas
// personalizadas, y quita la que dice referido"*).
//
// Vive en su propio módulo, igual que `policyStatuses.js` y `policyTypes.js`,
// por dos motivos: lo leen tres pantallas (el formulario de póliza, la tarjeta y
// Comisiones) sin tener que pasarse el dato de una a otra, y así se puede
// probar de verdad — antes estaba dentro de `PoliciesView.jsx`, que arrastra
// React, y las pruebas tenían que leer el texto fuente en vez de los valores.
//
// NO REABRE EL TEXTO LIBRE DEL #1822. Aquello era una casilla donde cada agente
// escribía el origen EN CADA PÓLIZA, y de ahí nacían gemelos ("Referido",
// "referido", "REFERIDO") que ensuciaban filtros y reportes. Esto se escribe UNA
// VEZ en los ajustes de la agencia y luego se ELIGE de la lista. El formulario
// sigue sin ninguna casilla donde teclearlo.

/**
 * Los que trae el CRM. "Referido" YA NO ESTÁ: se quitó a petición suya
 * (*"ya no se usa, quítelo"*). Sigue reconociéndose para LEER — ver
 * `LEAD_TYPES_SOLO_LECTURA` — pero no se ofrece.
 */
export const LEAD_TYPE_PRESETS = [
  { id: "personal",  es: "Personal",   en: "Personal",   icon: "👤" },
  { id: "facebook",  es: "Facebook",   en: "Facebook",   icon: "📘" },
  { id: "instagram", es: "Instagram",  en: "Instagram",  icon: "📸" },
  { id: "radio",     es: "Radio",      en: "Radio",      icon: "📻" },
  { id: "tv",        es: "TV",         en: "TV",         icon: "📺" },
  { id: "broker",    es: "Broker",     en: "Broker",     icon: "🤝" },
  { id: "website",   es: "Sitio web",  en: "Website",    icon: "🌐" },
];

/**
 * SOLO PARA LEER. Orígenes que ya no se ofrecen pero que siguen puestos en
 * pólizas de antes.
 *
 * Es el mismo criterio que ella misma fijó ayer con la opción "Otro": *"déjalo
 * pero no se podrán usar"*. Borrarlo del todo dejaría esas pólizas enseñando
 * `referido` en minúsculas y sin icono en la tarjeta, en Comisiones, en Hojas y
 * en el CSV de reclamos — y, peor, el formulario lo trataría como "escrito a
 * mano" y ofrecería una ✕ para borrarlo, invitando a tirar un dato que la
 * agencia sí puso a propósito.
 *
 * Mismo patrón que `_payment` en policyStatuses.js: se reconoce, no se ofrece.
 */
export const LEAD_TYPES_SOLO_LECTURA = [
  { id: "referido", es: "Referido", en: "Referral", icon: "🔗" },
];

// Los que añade la AGENCIA, además de los de fábrica. Se cargan de los ajustes
// al arrancar y se guardan ahí mismo: lo que se cree una vez lo encuentra la
// siguiente persona en la lista. Forma: { id, label, labelEs, icon }.
let _deLaAgencia = [];

export function setAgencyLeadSources(list) {
  _deLaAgencia = (Array.isArray(list) ? list : [])
    .filter((x) => x && x.id && (x.label || x.labelEs))
    .map((x) => ({
      id: String(x.id),
      label: String(x.label || x.labelEs),
      labelEs: String(x.labelEs || x.label),
      icon: String(x.icon || "🏷️"),
    }));
}
export function getAgencyLeadSources() { return _deLaAgencia.slice(); }

/**
 * Las OPCIONES elegibles: los de fábrica más los de la agencia. Los de solo
 * lectura NO entran aquí — esa es toda la diferencia entre "se puede poner" y
 * "se sabe leer".
 *
 * Sin repetidos: si alguien crea "Facebook" no nace un gemelo del de fábrica.
 */
export function allLeadSources() {
  const vistos = new Set(LEAD_TYPE_PRESETS.map((p) => p.id.toLowerCase()));
  const propios = _deLaAgencia
    .filter((x) => {
      const k = x.id.toLowerCase();
      if (vistos.has(k)) return false;
      vistos.add(k);
      return true;
    })
    .map((x) => ({ id: x.id, es: x.labelEs, en: x.label, icon: x.icon, propio: true }));
  return [...LEAD_TYPE_PRESETS, ...propios];
}

/** Todo lo que el CRM sabe leer, se ofrezca o no. */
export function todosLosOrigenesConocidos() {
  return [...allLeadSources(), ...LEAD_TYPES_SOLO_LECTURA];
}

/**
 * ¿Es un origen que el CRM reconoce? Lo usa el formulario para decidir si el
 * valor guardado es uno del catálogo o texto escrito a mano de antes del #1822.
 *
 * Mira TODO lo conocido, no solo lo elegible. Si mirara solo lo elegible, las
 * pólizas marcadas "Referido" pasarían a verse como escritas a mano el día que
 * se quitó del catálogo.
 */
export function esOrigenConocido(val) {
  if (!val) return false;
  return todosLosOrigenesConocidos().some((x) => x.id === val);
}

/** La etiqueta bonita de un origen guardado; si no se reconoce, el texto crudo. */
export function leadTypeLabel(val, lang) {
  if (!val) return "";
  const p = todosLosOrigenesConocidos().find((x) => x.id === val);
  return p ? `${p.icon} ${lang === "es" ? p.es : p.en}` : val;
}

/**
 * El id de un origen nuevo, a partir del nombre que escriba la agencia.
 *
 * Se genera del texto y no de la fecha para que "Feria de salud" cree
 * `feria_de_salud` — legible en la base, en los reportes y en el CSV, donde lo
 * que se guarda es el id. Si al limpiarlo no queda nada (un nombre solo con
 * emojis o en otro alfabeto) se cae a un id genérico, que es feo pero funciona;
 * dejar el id vacío haría que la ficha no se pudiera elegir.
 */
export function idParaOrigenNuevo(nombre, existentes) {
  const base = String(nombre || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  const raiz = base || "origen";
  const usados = new Set((existentes || []).map((x) => String(x).toLowerCase()));
  if (!usados.has(raiz)) return raiz;
  let n = 2;
  while (usados.has(`${raiz}_${n}`)) n += 1;
  return `${raiz}_${n}`;
}
